import { computed, inject, type MaybeRef, unref } from "vue";

import { apiFetch, useOfflineQuery } from "@ja/portal-query";

import { preview as previewRoute } from "../wayfinder/routes";

interface PreviewResponse {
  item: Record<string, unknown>;
  model: string;
  id: string;
}

export interface UseQuickViewerDataOptions {
  /** Model name for the preview endpoint */
  model: string;
  /** Record ID to fetch */
  id: string | number;
  /** Initial item data from parent (used while fetching) */
  initialItem?: Record<string, unknown>;
  /** Whether to enable fetching (e.g., only when popover is open) */
  enabled?: MaybeRef<boolean>;
}

/**
 * Composable for fetching QuickViewer data with offline caching.
 *
 * Uses portal-offline's useOfflineQuery for:
 * - Automatic caching (5 min stale time online, 30 min offline)
 * - Stale-while-revalidate pattern
 * - Offline support
 *
 * @example
 * ```ts
 * const { item, isLoading, isFromCache } = useQuickViewerData({
 *   model: 'user',
 *   id: 123,
 *   initialItem: { id: 123, name: 'John' },
 *   enabled: isPopoverOpen,
 * });
 * ```
 */
export function useQuickViewerData(options: UseQuickViewerDataOptions) {
  const { model, id, initialItem, enabled } = options;

  // Inject portal name from parent (provided by portal layout)
  const portalName = inject<string>("portalName", "portal-crudmanager");

  // Build the preview URL using wayfinder route
  const previewUrl = previewRoute.url({ model, id: String(id) });

  const {
    data,
    isLoading,
    isFetching,
    isFromCache,
    cacheAge,
    cacheTimestamp,
    refetch,
    invalidateCache
  } = useOfflineQuery<PreviewResponse>(
    ["crud-preview", model, String(id)],
    () => apiFetch<PreviewResponse>(previewUrl),
    {
      portalName,
      offlineKey: `preview:${model}:${id}`,
      staleTime: 5 * 60 * 1000, // 5 min online
      offlineStaleTime: 30 * 60 * 1000, // 30 min offline
      enabled: unref(enabled) ?? true
    }
  );

  // Merge fetched data with initial data
  const item = computed<Record<string, unknown>>(() => {
    return data.value?.item ?? initialItem ?? { id };
  });

  // Format cache age for display
  const formattedCacheAge = computed(() => {
    const age = cacheAge.value;
    if (!age) return null;

    const seconds = Math.floor(age / 1000);
    if (seconds < 60) return `${seconds}s`;

    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m`;

    const hours = Math.floor(minutes / 60);
    return `${hours}h`;
  });

  return {
    /** The record item data (fetched or initial) */
    item,
    /** Whether the query is in initial loading state */
    isLoading,
    /** Whether the query is currently fetching (including background) */
    isFetching,
    /** Whether the data is from offline cache */
    isFromCache,
    /** Cache age in milliseconds */
    cacheAge,
    /** Cache timestamp */
    cacheTimestamp,
    /** Human-readable cache age (e.g., "2m", "1h") */
    formattedCacheAge,
    /** Refetch data from server */
    refetch,
    /** Invalidate the cache and refetch */
    invalidateCache
  };
}
